import { useState, useEffect } from 'react';
import {
  QuestionnaireAssignment,
  QuestionnaireAssignmentStore,
  QuestionnaireFormOption,
  QuestionnaireScheduleOption,
} from '@app/api';
import { defaultAssignments } from '@app/components/modals/questionnaireAssignments';

/**
 * What the cohort dialog needs to assign questionnaires: every questionnaire with a published
 * version, every schedule, and the cohort's current assignments. A new cohort starts with every
 * questionnaire on the default schedule.
 *
 * loaded is only true once all of it arrived. Until then the dialog must not save assignments,
 * or a failed load would save an empty selection and take the cohort's questionnaires away.
 */
export const useCohortQuestionnaires = (cohortId: number, enabled: boolean) => {
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireFormOption[]>([]);
  const [schedules, setSchedules] = useState<QuestionnaireScheduleOption[]>([]);
  const [assignments, setAssignments] = useState<QuestionnaireAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const loadQuestionnaires = async () => {
      setLoading(true);
      setLoaded(false);
      try {
        const store = new QuestionnaireAssignmentStore();
        const [formsResponse, schedulesResponse, assignmentsResponse] = await Promise.all([
          store.GetForms(),
          store.GetSchedules(),
          cohortId > 0 ? store.GetCohortAssignments(cohortId) : Promise.resolve(null),
        ]);
        const assignmentsLoaded = !assignmentsResponse || assignmentsResponse.status === 'SUCCESS';
        if (formsResponse.status !== 'SUCCESS' || schedulesResponse.status !== 'SUCCESS' || !assignmentsLoaded) {
          console.error('Failed to load questionnaires');
          return;
        }
        const forms = formsResponse.result ?? [];
        const allSchedules = schedulesResponse.result ?? [];
        setQuestionnaires(forms);
        setSchedules(allSchedules);
        setAssignments(
          assignmentsResponse ? (assignmentsResponse.result ?? []) : defaultAssignments(forms, allSchedules)
        );
        setLoaded(true);
      } catch (error) {
        console.error('Failed to load questionnaires:', error);
      } finally {
        setLoading(false);
      }
    };

    loadQuestionnaires();
  }, [cohortId, enabled]);

  return { questionnaires, schedules, assignments, loading, loaded };
};
